<template>
  <div>
    <v-dialog v-model="signatureConfirmDialog" max-width="400">
      <div class="text-center">
        <v-sheet
          class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
          color="blue-grey darken-3"
          style="width: 100%"
        >
          <div class="grey--text text--lighten-1 text-body-2 mb-4">
            {{ $t('Gostaria de assinar como responsável?') }}
          </div>

          <div class="d-flex justify-center">
            <v-radio-group v-model="option" dark>
              <v-radio :value="0">
                <template v-slot:label>
                  <span class="grey--text text-body-2">
                    {{ $t('Usar assinatura do usuário logado') }}
                  </span>
                </template>
              </v-radio>
              <v-radio :value="1">
                <template v-slot:label>
                  <span class="grey--text text-body-2">
                    {{ $t('Assinar via touchscreen') }}
                  </span>
                </template>
              </v-radio>
            </v-radio-group>
          </div>

          <v-expand-transition>
            <div v-if="option == 1">
              <SignatureScreen
                v-model="checklist.validatorName"
                @save="validateChecklist"
              />
            </div>
          </v-expand-transition>

          <v-btn
            v-if="option == 0 || option == 2"
            :disabled="loading"
            depressed
            class="ma-1"
            color="grey"
            @click="signatureConfirmDialog = false"
          >
            {{ $t('Não') }}
          </v-btn>

          <v-btn
            v-if="option == 0 || option == 2"
            :loading="loading"
            class="ma-1"
            depressed
            color="#B14949"
            @click="validateChecklist"
          >
            {{ $t('Sim') }}
          </v-btn>
        </v-sheet>
      </div>
    </v-dialog>

    <v-card class="card-pad d-flex align-center mb-4">
      <v-row class="ma-0" dense>
        <v-col cols="12">
          <div class="my-3 d-flex justify-center align-center header">
            <img
              :src="
                checklist.institution && checklist.institution.logoPath
                  ? checklist.institution.logoPath
                  : 'https://i.imgur.com/yItTg2c.png'
              "
              style="object-fit: cover; max-width: 50px"
            />
            <h1 class="ml-5">
              <strong class="text2" style="vertical-align: bottom">
                Checklist
              </strong>
              <strong class="text1" style="vertical-align: bottom">
                {{ $t('Rotina de Inspeção') }}
              </strong>
            </h1>
          </div>
        </v-col>
        <v-col cols="12" sm="6">
          <strong>{{ $t('Instituição') }}: </strong>
          <span> {{ checklist.institution.name }}</span>
        </v-col>
        <v-col cols="12" sm="6">
          <strong>{{ $t('Unidade') }}: </strong>
          <span> {{ checklist.serviceOrder.unidade.name }}</span>
        </v-col>
        <v-col cols="12" sm="6">
          <strong>{{ $t('Nº O.S.') }}: </strong>
          <span> {{ checklist.serviceOrder.number }}</span>
        </v-col>
        <v-col cols="12" sm="6" v-if="checklist.answerTemplate.type == 1">
          <strong>{{ $t('Setor') }}: </strong>
          <span> {{ checklist.serviceOrder.department.name }}</span>
        </v-col>
        <v-col cols="12" sm="6" v-else>
          <strong>{{ $t('Equipamento padrão') }}: </strong>
          <span>
            {{
              checklist.answerTemplate.standardEquipment
                ? checklist.answerTemplate.standardEquipment.name
                : ''
            }}</span
          >
        </v-col>
        <v-col
          offset="6"
          cols="12"
          sm="6"
          v-if="checklist.answerTemplate.withoutEquipment"
        >
          <strong>{{ $t('Localização') }}: </strong>
          <span>
            {{
              checklist.serviceOrder ? checklist.serviceOrder.location : ''
            }}</span
          >
        </v-col>
      </v-row>
    </v-card>

    <v-card class="card-container mb-4">
      <strong class="ml-1">Itens do checklist</strong>
      <v-data-table
        mobile-breakpoint="0"
        class="main-table mt-4"
        :loading-text="$t('Carregando...')"
        :no-data-text="$t('Nenhum item encontrado')"
        hide-default-footer
        hide-default-header
        :headers="headers"
        :items="checklist.answerTemplate.items"
        disable-filtering
        :group-by="['equipment.id', 'equipment.name']"
        disable-pagination
      >
        <template #header="{ props, on }">
          <thead>
            <tr>
              <th
                class="text-center"
                v-for="header in props.headers"
                :key="header.value"
              >
                <v-simple-checkbox
                  v-if="header.value === 'data-table-select'"
                  v-model="props.everyItem"
                  :indeterminate="props.someItems && !props.everyItem"
                  color="purple"
                  @input="on['toggle-select-all']"
                ></v-simple-checkbox>

                <div>
                  <v-hover v-slot="{ hover }">
                    <div>
                      <span>{{ $t(header.text) }}</span>
                      <v-icon
                        v-if="
                          hover &&
                          header.value != 'data-table-select' &&
                          header.value != 'actions'
                        "
                        color="white"
                        class="mx-2"
                        @click="on.sort(header.value)"
                      >
                        {{
                          props.options.sortBy.includes(header.value)
                            ? `mdi-chevron-down`
                            : `mdi-chevron-up`
                        }}
                      </v-icon>
                    </div>
                  </v-hover>
                </div>
              </th>
            </tr>
          </thead>
        </template>

        <template #[`group.header`]="{ group }">
          <th
            colspan="8"
            v-if="
              checklist.answerTemplate.type == 1 &&
              checklist.answerTemplate.withoutEquipment == false
            "
          >
            <span class="ml-6">
              {{
                checklist.answerTemplate.items.find(
                  (x) => x.equipment.id == group
                ).equipment.name
              }}
            </span>
          </th>
        </template>

        <template #[`item.checklistItemYes`]="{ item }">
          <v-icon :color="item.response == 0 ? '#BBCF20' : ''">
            {{
              item.response == 0
                ? 'mdi-checkbox-marked-circle-outline'
                : 'mdi-checkbox-blank-circle-outline'
            }}
          </v-icon>
        </template>
        <template #[`item.checklistItemNo`]="{ item }">
          <v-icon :color="item.response == 1 ? '#B14949' : ''">
            {{
              item.response == 1
                ? 'mdi-checkbox-marked-circle-outline'
                : 'mdi-checkbox-blank-circle-outline'
            }}
          </v-icon>
        </template>

        <template #[`item.checklistItemNotApplicaton`]="{ item }">
          <v-icon :color="item.response == 2 ? '#426483' : ''">
            {{
              item.response == 2
                ? 'mdi-checkbox-marked-circle-outline'
                : 'mdi-checkbox-blank-circle-outline'
            }}
          </v-icon>
        </template>

        <template #[`item.observation`]="{ item }">
          {{ item.observation ? item.observation : $t('Sem registros') }}
        </template>
      </v-data-table>

      <div class="mt-6 d-flex flex-wrap">
        <strong>{{ $t('Data do checklist') }}: </strong>
        <span class="ml-2">
          {{
            checklist.executionDate
              ? formatDate(checklist.executionDate)
              : $t('Sem registro')
          }}
        </span>
        <v-spacer></v-spacer>
        <strong>C</strong> = {{ $t('Conforme') }} <span class="mx-2"> / </span>
        <strong>NC</strong> = {{ $t('Não conforme') }}
      </div>
    </v-card>

    <v-card class="card-pad mb-4">
      <div class="d-flex flex-wrap">
        <div class="d-flex flex-wrap flex-column align-center text-center">
          <img
            style="max-width: 320px; max-height: 100px"
            class="my-4"
            :src="
              checklist && checklist.executorImage
                ? checklist.executorImage
                : checklist &&
                  checklist.executingUser &&
                  checklist.executingUser.signatureImage
                ? checklist.executingUser.signatureImage
                : ''
            "
          />
          <span>{{ $t('Assinatura Técnico Executor') }}</span>
          <br />

          <span
            v-if="
              checklist.executorImage == null && checklist.executorName == null
            "
            >{{
              checklist.executingUser
                ? checklist.executingUser.name
                : $t('Sem registro')
            }}</span
          >

          <span v-if="checklist.executorName">{{
            checklist.executorName
          }}</span>

          <span
            v-if="
              checklist.executingUser &&
              checklist.executingUser.professionalRegistration
            "
          >
            {{
              'Registro Profissional: ' +
              (currentServiceOrder?.executingUser?.professionalRegistration ||
                'Não informado')
            }}
          </span>
        </div>
        <v-spacer></v-spacer>

        <div class="d-flex flex-column align-center text-center fill-height">
          <img
            style="max-width: 320px; max-height: 100px"
            class="my-4"
            :src="
              checklist && checklist.validatorImage
                ? checklist.validatorImage
                : checklist &&
                  checklist.validatorUser &&
                  checklist.validatorUser.signatureImage
                ? checklist.validatorUser.signatureImage
                : ''
            "
          />
          <span>Assinatura Responsável</span>
          <br />

          <span v-if="checklist.validatorName">{{
            checklist.validatorName
          }}</span>

          <span v-else>{{
            checklist.validatorUser
              ? checklist.validatorUser.name
              : $t('Sem registro')
          }}</span>

          <span
            v-if="
              checklist.validatorUser &&
              checklist.validatorUser.professionalRegistration
            "
          >
            {{
              'Registro Profissional: ' +
              (currentServiceOrder?.validatorUser?.professionalRegistration ||
                'Não informado')
            }}
          </span>
        </div>
      </div>

      <div class="d-flex justify-center text-center text-caption">
        <div style="max-width: 300px" class="mt-6">
          {{ checklist.footnote }}
        </div>
      </div>

      <div class="d-flex justify-center mt-4">
        <v-btn
          v-if="checklist && checklist.isValidatorSigned"
          @click="removeValidateChecklist"
          class="secondary-button"
          color="#B14949"
          depressed
        >
          {{ $t('Remover Validação') }}
        </v-btn>
        <v-btn
          v-else
          @click="signatureConfirmDialog = true"
          class="secondary-button"
          depressed
        >
          {{ $t('Validar') }}
        </v-btn>

        <v-btn class="primary-button mr-0" depressed @click="print">
          {{ $t('Imprimir') }}
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<style scoped>
.text1 {
  color: #426483;
}

.text2 {
  color: #bbcf20;
}
</style>

<script>
import ApiService from '../../../../../services/ApiService'
import Utils from '../../../../../Utils/FormatDate'
import SignatureScreen from '../../../../../components/SignatureScreen.vue'

export default {
  name: 'PrintRoutinePage',

  components: {
    SignatureScreen,
  },

  created() {
    this.user = JSON.parse(localStorage.getItem('user'))
    this.institution = JSON.parse(localStorage.getItem('institution'))
    this.env = process.env.VUE_APP_BASE_URL
    this.checklistId = this.$route.query.id

    if (this.checklistId) {
      this.getChecklist()
    }
  },

  data() {
    return {
      institution: {},
      option: false,
      loading: false,
      signatureConfirmDialog: false,
      env: '',
      checklistId: '',
      headers: [
        {
          text: '',
          align: 'center',
          value: '',
          sortable: false,
        },
        {
          text: '',
          align: 'center',
          value: 'equipment.identification',
          sortable: false,
        },
        {
          text: 'Item',
          align: 'center',
          value: 'item.name',
          sortable: false,
        },
        {
          text: 'C',
          align: 'center',
          value: 'checklistItemYes',
          width: '50',
          sortable: false,
        },
        {
          text: 'NC',
          align: 'center',
          value: 'checklistItemNo',
          width: '50',
          sortable: false,
        },
        {
          text: 'N/A',
          align: 'center',
          value: 'checklistItemNotApplicaton',
          width: '50',
          sortable: false,
        },
        {
          text: 'Observação',
          align: 'center',
          value: 'observation',
          width: '300',
          sortable: false,
        },
      ],
      apiService: new ApiService(),
      checklist: {
        institution: {},
        validatorUser: {},
        executingUser: {},
        answerTemplate: {
          sector: {},
          standardEquipment: {},
        },
        serviceOrder: {
          unidade: {},
        },
      },
      validatorSignatureImage: null,
      user: {},
    }
  },

  methods: {
    async getUser(userId) {
      await this.apiService.get(`user/${userId}`).then((response) => {
        this.validatorSignatureImage = response.content.signatureImage.includes(
          'https://equipacarefix.blob.core.windows.net/'
        )
          ? response.content.signatureImage
          : `data:image/png;base64,${response.content.signatureImage}`
      })
    },

    print() {
      this.apiService
        .post(`inspectionRoutineChecklist/print/${this.checklistId}`)
        .then((response) => {
          var pdfResult = `data:application/pdf;base64,${response.content}`
          const link = document.createElement('a')
          link.href = pdfResult
          link.setAttribute('download', `ChecklistInspecao.pdf`)
          document.body.appendChild(link)
          link.click()
        })
    },

    async removeValidateChecklist() {
      let valid = false

      if (
        this.institution.userType == 2 ||
        this.user.mainType == 1 ||
        this.institution.userType == 8 ||
        this.user.mainType == 0
      ) {
        valid = true
      }

      if (this.user.id == this.checklist.validatorUserId) {
        valid = true
      }

      if (valid) {
        this.validatorSignatureImage = null

        const checklist = {
          Id: this.checklist.id,
          isValidatorSigned: false,
          ValidatorUserId: null,
        }

        this.validate(checklist)
      } else {
        this.$toast.error('Você não tem permissão para remover validações')
      }
    },

    async validate(checklist) {
      this.apiService
        .post('inspectionRoutineChecklist/validate', checklist)
        .then((resp) => {
          this.getChecklist()
          this.signatureErrorDialog = true
        })
    },

    async validateChecklist(data) {
      if (this.option == 0) {
        if (this.user.signatureImage) {
          const checklist = {
            Id: this.checklist.id,
            isValidatorSigned: true,
            ValidatorUserId: this.user.id,
          }
          this.validate(checklist)
          this.signatureConfirmDialog = false
        } else {
          this.signatureErrorDialog = true
        }
      } else if (this.option == 1) {
        const checklist = {
          Id: this.checklist.id,
          isValidatorSigned: true,
          ValidatorUserId: this.user.id,
          validatorImage: data,
          validatorName: this.checklist.validatorName,
        }
        this.validate(checklist)
        this.signatureConfirmDialog = false
      } else {
        if (
          this.checklist &&
          this.checklist.serviceOrder &&
          this.checklist.serviceOrder &&
          this.checklist.serviceOrder.responsibleName
        ) {
          this.apiService
            .get(`byName/${this.checklist.serviceOrder.responsibleName}`)
            .then((response) => {
              const checklist = {
                Id: this.checklist.id,
                isValidatorSigned: true,
                ValidatorUserId: response.content.id,
                validatorName: response.content.name,
              }
              this.validate(checklist)
              this.signatureConfirmDialog = false
            })
        } else {
          this.$toast.error(
            'Nenhum responsável encontrado na Ordem de Serviço!'
          )
        }
      }
    },

    formatDate(date) {
      return Utils.dateTime(date)
    },

    getChecklist() {
      this.loading = true

      this.apiService
        .get(`inspectionRoutineChecklist/toPrint/${this.checklistId}`)
        .then((response) => {
          this.checklist = { ...this.checklist, ...response.content }

          const answeredItems = this.checklist.answerTemplate.items.filter(
            (x) => x.response != null
          )
          if (this.checklist.executingUser != null) {
            this.checklist.executingUser.signatureImage =
              this.checklist.executingUser.signatureImage.includes(
                'https://equipacarefix.blob.core.windows.net/'
              )
                ? this.checklist.executingUser.signatureImage
                : `${env}Uploads/Users/${this.checklist.executingUser.id}/signatureImage/image_1.png`
          }

          if (answeredItems.length == 0) {
            this.$router.push(
              `/inspectionRoutineChecklist/manage?id=${this.checklistId}`
            )
          }

          if (
            this.checklist.answerTemplate.type == 1 &&
            this.checklist.answerTemplate.withoutEquipment == false
          ) {
            this.headers.splice(1, 0, {
              text: 'Equipamento',
              align: 'center',
              value: 'equipment.name',
              sortable: false,
            })
            this.headers.splice(1, 0, {
              text: 'Identificação',
              value: 'equipment.identification',
              sortable: false,
            })

            if (this.checklist.isValidatorSigned) {
              this.getUser(this.checklist.validatorUserId)
            }
          }

          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style></style>
